import React from 'react';
import { Input, Select } from '@fluentui/react-components';
import { format, add } from 'date-fns';
import { useSelector } from 'react-redux';
import NewProjectWizardCard from './NewProjectWizardCard';
import styles from './css/index.module.css';
import { resolveWorkspaceProjectTypeSelectOptions } from '../../utils/projectTypes';
import { resolveWorkspaceInvestmentTypeOptions } from '../../utils/projectMetadataOptions';
import { getCreateStep1FieldPolicy, shouldShowInvestmentType } from '../../utils/projectTypeFieldPolicy';
import { ensureHttpsWebsite, isWebsiteUrl } from '../../utils/websiteUrl';

export function createInitialStep1FormValues() {
    return {
        project_name: '',
        project_type: '',
        company_name: '',
        website: '',
        investment_type: '',
        investor_name: '',
        project_deadline: format(add(new Date(), { months: 1 }), 'yyyy-MM-dd'),
        context_files: [],
        sector: '',
        description: '',
        company_size: '',
        established_year: '',
        customer_stage: '',
        customer_type: '',
        customer_segment: '',
        currency: 'GBP',
        revenue: '',
        linkedin: '',
        product_website: '',
        companieshouse: '',
        logo: '',
        logo_name: '',
        fieldMeta: {},
        engagement_objective: '',
        scope_areas: [],
        engagementSeeded: false,
    };
}

const CreateProjectStep1 = ({ formData, onChange, onContinue, onCancel, continueLoading }) => {
    const memberships = useSelector(state => state?.user?.memberships || []);
    const activeWorkspaceId = useSelector(state => state?.user?.activeWorkspaceId) || localStorage.getItem('active_workspace_id');

    const activeTenant = React.useMemo(() => {
        const membership = memberships.find(mem => mem?.workspace?.id === activeWorkspaceId);
        return membership?.workspace || null;
    }, [memberships, activeWorkspaceId]);

    const projectTypeOptions = React.useMemo(
        () => resolveWorkspaceProjectTypeSelectOptions(activeTenant, { includeValue: formData?.project_type }),
        [activeTenant, formData?.project_type],
    );

    const investmentTypeOptions = React.useMemo(
        () => resolveWorkspaceInvestmentTypeOptions(activeTenant, { includeValue: formData?.investment_type }),
        [activeTenant, formData?.investment_type],
    );

    const fieldPolicy = getCreateStep1FieldPolicy(formData?.project_type);
    const showInvestmentType = shouldShowInvestmentType(fieldPolicy);

    const handleChange = e => {
        const { name, value } = e.target;
        if (name === 'project_type') {
            const nextPolicy = getCreateStep1FieldPolicy(value);
            onChange({
                project_type: value,
                ...(nextPolicy.investmentType === 'hidden' ? { investment_type: '', investor_name: '' } : {}),
            });
            return;
        }
        onChange({ [name]: value });
    };

    const handleWebsiteBlur = () => {
        const next = ensureHttpsWebsite(formData.website);
        if (next && next !== formData.website && isWebsiteUrl(next)) {
            onChange({ website: next });
        }
    };

    const showUrlError = formData?.website?.trim() && !isWebsiteUrl(formData.website);

    const continueDisabled =
        !formData?.project_name?.trim() ||
        !formData?.project_type ||
        !formData?.company_name?.trim() ||
        showUrlError ||
        (fieldPolicy.investmentType === 'required' && !formData?.investment_type);

    return (
        <NewProjectWizardCard
            step={1}
            title="Create new project"
            subtitle="Start with the essentials. Lens will help complete the project context."
            onContinue={onContinue}
            onCancel={onCancel}
            continueDisabled={continueDisabled}
            continueLoading={continueLoading}
        >
            <div className={styles.formGrid}>
                <div className={styles.fieldBlock}>
                    <p className={styles.fieldLabel}>
                        Project Name <span className={styles.required}>*</span>
                    </p>
                    <Input
                        className={styles.textInput}
                        name="project_name"
                        value={formData.project_name}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className={styles.fieldBlock}>
                    <p className={styles.fieldLabel}>
                        Project Type <span className={styles.required}>*</span>
                    </p>
                    <Select className={styles.selectField} name="project_type" value={formData.project_type} onChange={handleChange} required>
                        <option value="">Select</option>
                        {projectTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.text}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className={styles.fieldBlock}>
                    <p className={styles.fieldLabel}>
                        Organisation Name <span className={styles.required}>*</span>
                    </p>
                    <Input
                        className={styles.textInput}
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className={styles.fieldBlock}>
                    <p className={styles.fieldLabel}>Organisation Website</p>
                    <Input
                        className={styles.textInput}
                        name="website"
                        type="url"
                        value={formData.website}
                        onChange={handleChange}
                        onBlur={handleWebsiteBlur}
                        placeholder="https://"
                    />
                    <p className={styles.fieldHint}>Helps Lens automatically find company information.</p>
                    {showUrlError ? <span className={styles.errorMessage}>Please enter a URL in https:// or http:// format</span> : null}
                </div>
                {showInvestmentType ? (
                    <div className={styles.fieldBlock}>
                        <p className={styles.fieldLabel}>
                            Investment Type
                            {fieldPolicy.investmentType === 'required' ? <span className={styles.required}> *</span> : null}
                        </p>
                        <Select
                            className={styles.selectField}
                            name="investment_type"
                            value={formData.investment_type}
                            onChange={handleChange}
                            required={fieldPolicy.investmentType === 'required'}
                        >
                            <option value="">Select</option>
                            {investmentTypeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.text}
                                </option>
                            ))}
                        </Select>
                    </div>
                ) : null}
                {fieldPolicy.showInvestor ? (
                    <div className={styles.fieldBlock}>
                        <p className={styles.fieldLabel}>{fieldPolicy.investorLabel}</p>
                        <Input
                            className={styles.textInput}
                            name="investor_name"
                            value={formData.investor_name}
                            onChange={handleChange}
                        />
                    </div>
                ) : null}
                <div className={styles.fieldBlock}>
                    <p className={styles.fieldLabel}>Project Deadline</p>
                    <Input
                        className={styles.textInput}
                        name="project_deadline"
                        type="date"
                        value={formData.project_deadline}
                        onChange={handleChange}
                    />
                </div>
            </div>
        </NewProjectWizardCard>
    );
};

export default CreateProjectStep1;
